

import React from 'react';
import { ChevronRight, CalendarX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { statusBadge } from '@/lib/data';
import { daysBetween, dayRange, parseDay, statusLabel, type DashboardEvent } from '../../useDashboard';

interface EventsWidgetProps {
  today: string;
  todayEvents: DashboardEvent[];
  upcomingEvents: DashboardEvent[];
  upcomingCount: number;
}

function whenLabel(e: DashboardEvent, today: string): { text: string; tone: string } | null {
  if (!e.start_date) return null;
  const offset = daysBetween(today, e.start_date);
  if (offset < 0) return { text: 'Ongoing', tone: 'bg-amber-600 text-white' };
  if (offset === 0) return { text: 'Today', tone: 'bg-amber-600 text-white' };
  if (offset === 1) return { text: 'Tomorrow', tone: 'bg-amber-100 text-amber-800' };
  if (offset <= 7) return { text: `in ${offset} days`, tone: 'bg-amber-100 text-amber-800' };
  return { text: `in ${offset} days`, tone: 'bg-stone-100 text-stone-600' };
}

function EventRow({ event, today }: { event: DashboardEvent; today: string }) {
  const start = parseDay(event.start_date);
  const when = whenLabel(event, today);
  const details = [event.client?.name, event.venue?.name, event.guest_count ? `${event.guest_count} guests` : ''].filter(Boolean).join(' · ');

  return (
    <Link
      to={`/events/detail?id=${event.id}`}
      className="flex items-center gap-3 min-h-[56px] p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-colors group"
    >
      <div className="flex-shrink-0 w-12 rounded-lg bg-amber-50 text-center py-1.5">
        <div className="text-[10px] font-semibold uppercase text-amber-700">
          {start ? start.toLocaleDateString('en-IN', { month: 'short' }) : '—'}
        </div>
        <div className="text-lg font-bold leading-tight text-amber-900">{start ? start.getDate() : '?'}</div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-stone-800 truncate group-hover:text-amber-700">{event.name || 'Untitled event'}</p>
        <p className="text-xs text-stone-500 truncate mt-0.5">
          {details || (event.event_type ?? 'No client yet')}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {when && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${when.tone}`}>{when.text}</span>}
          {event.end_date && event.end_date !== event.start_date && (
            <span className="text-[11px] text-stone-500">{dayRange(event.start_date, event.end_date)}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`hidden sm:inline-block text-xs px-2 py-0.5 rounded-full ${statusBadge(event.status)}`}>
          {statusLabel(event.status)}
        </span>
        <ChevronRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600" />
      </div>
    </Link>
  );
}

export function EventsWidget({ today, todayEvents, upcomingEvents, upcomingCount }: EventsWidgetProps) {
  const nothing = todayEvents.length === 0 && upcomingEvents.length === 0;

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-stone-800">Coming up</h3>
        <Link to="/calendar" className="inline-flex items-center h-10 px-2 -mr-2 text-sm text-amber-700 hover:text-amber-800 font-medium">
          Calendar
        </Link>
      </div>

      {nothing ? (
        <div className="text-center py-8">
          <CalendarX className="w-10 h-10 mx-auto mb-2 text-stone-300" />
          <p className="text-sm text-stone-600 font-medium">Nothing booked from today on</p>
          <Link to="/events" className="mt-2 inline-flex items-center h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">
            Open events
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {todayEvents.length > 0 && (
            <section>
              <h4 className="text-[11px] font-bold uppercase tracking-wide text-stone-400 mb-2">
                Today <span className="font-normal">· {todayEvents.length}</span>
              </h4>
              <div className="space-y-2">
                {todayEvents.map((e) => <EventRow key={e.id} event={e} today={today} />)}
              </div>
            </section>
          )}

          <section>
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-stone-400 mb-2">
              Next <span className="font-normal">· {upcomingCount} booked after today</span>
            </h4>
            {upcomingEvents.length === 0 ? (
              <p className="text-sm text-stone-500 py-3">No events booked after today.</p>
            ) : (
              <div className="space-y-2">
                {upcomingEvents.map((e) => <EventRow key={e.id} event={e} today={today} />)}
              </div>
            )}
          </section>

          {upcomingCount > upcomingEvents.length && (
            <Link to="/events" className="flex items-center justify-center gap-1 h-10 rounded-lg border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
              See all {upcomingCount} upcoming events <ChevronRight className="w-4 h-4 text-stone-400" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
